import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchInvoices } from "../../redux/invoicesSlice";
import SkeletonRow from "../../components/designLayouts/skeletons/rowSkeleton";
import formatPrice from "../../utils/formatPrice";

const UnpaidInvoices = () => {
  const dispatch = useDispatch();
  const { items: invoices, status } = useSelector((state) => state.invoices);
  const precision = sessionStorage.getItem("prec") || 0;

  useEffect(() => {
    dispatch(fetchInvoices());
  }, [dispatch]);

  // Group invoices by currency (CUR) and calculate totals per group
  const groupTotals = invoices.reduce((acc, invoice) => {
    const cur = invoice?.symbol || "Unknown";
    const net = Number(invoice?.netamount?.replace(/,/g, "")) || 0;
    const due = Number(invoice?.dueamount?.replace(/,/g, "")) || 0;
    if (!acc[cur]) {
      acc[cur] = { netTotal: 0, dueTotal: 0 };
    }
    acc[cur].netTotal += net;
    acc[cur].dueTotal += due;
    return acc;
  }, {});

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gray-100 px-4">
        <div className="mx-auto bg-white shadow-md rounded-lg p-6">
          <h1 className="text-xl font-semibold text-gray-800 mb-4">
            Unpaid Invoices
          </h1>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-200 text-gray-600 uppercase">
                  <th className="py-3 px-4 text-left whitespace-nowrap">
                    Invoice #
                  </th>
                  <th className="py-3 px-4 text-center whitespace-nowrap">
                    Date
                  </th>
                  <th className="py-3 px-4 text-center whitespace-nowrap">
                    Due Date
                  </th>
                  <th className="py-3 px-4 text-center whitespace-nowrap">
                    Delay (Days)
                  </th>
                  <th className="py-3 px-4 text-center whitespace-nowrap">
                    CUR
                  </th>
                  <th className="py-3 px-4 text-right whitespace-nowrap">
                    Net Amount
                  </th>
                  <th className="py-3 px-4 text-right whitespace-nowrap">
                    Due Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, i) => (
                  <SkeletonRow key={i} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (status === "failed") throw new Error("Could not fetch Unpaid Invoices");

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4">
      <div className="mx-auto bg-white shadow-md rounded-lg p-6">
        <h1 className="text-xl font-semibold text-gray-800 mb-4">
          Unpaid Invoices
        </h1>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-200 text-sm">
            <thead>
              <tr className="bg-gray-200 text-gray-600 uppercase">
                <th className="py-3 px-4 text-left whitespace-nowrap">
                  Invoice #
                </th>
                <th className="py-3 px-4 text-center whitespace-nowrap">
                  Date
                </th>
                <th className="py-3 px-4 text-center whitespace-nowrap">
                  Due Date
                </th>
                <th className="py-3 px-4 text-center whitespace-nowrap">
                  Delay (Days)
                </th>
                <th className="py-3 px-4 text-center whitespace-nowrap">CUR</th>
                <th className="py-3 px-4 text-right whitespace-nowrap">
                  Net Amount
                </th>
                <th className="py-3 px-4 text-right whitespace-nowrap">
                  Due Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice, index) => (
                <tr
                  key={invoice?.doc_num}
                  className={`border-b ${index % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                >
                  <td className="py-3 px-4 whitespace-nowrap">
                    {invoice?.doc_num}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap text-center">
                    {invoice?.date}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap text-center">
                    {invoice?.duedate}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap text-center">
                    {invoice?.delay}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap text-center">
                    {invoice?.symbol}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap text-right">
                    {formatPrice(
                      Number(invoice?.netamount)?.toFixed(precision),
                    )}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap text-right">
                    {formatPrice(
                      Number(invoice?.dueamount)?.toFixed(precision),
                    )}
                  </td>
                </tr>
              ))}
              {/* Totals Rows for Each CUR */}
              {Object.entries(groupTotals).map(([cur, totals]) => (
                <tr key={cur} className="bg-gray-100 font-semibold">
                  <td colSpan="4" className="py-3 px-4 text-left"></td>
                  <td className="py-2 px-4 text-center">{cur}</td>
                  <td className="py-2 px-4 text-right">
                    {formatPrice(totals.netTotal.toFixed(precision))}
                  </td>
                  <td className="py-2 px-4 text-right">
                    {formatPrice(totals.dueTotal.toFixed(precision))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UnpaidInvoices;
